import { Stack, SxProps, Typography } from '@mui/material';
import { Header } from 'common/ui/Header';
import { Paths } from 'services/navigation/paths';
import { useNavigate } from 'react-router-dom';
import { TableV2 } from 'components/Table/TableV2';

import { TemplateItem } from 'modules/analyse-templates/types/TemplateItem';
import {
  useGetAutomationTasksTemplateByOrganizationQuery,
  useDeleteAutomationTasksTemplateMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import React, { useMemo, useState } from 'react';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { Button } from 'system/atoms';
import { TemplateSortField } from 'modules/analyse-templates/constants/sortConstants';
import { useTableSort } from 'common/hooks/useTableSort';
import { SearchableSortDirection } from 'generated-graphql/types';
import { FailedMessageWithHref, SearchInput } from 'common/ui';
import { useConfirmationModal } from 'services/modals/hooks';
import { ToastType, useToast } from 'services/toast';
import { useLoadingOverlay } from 'services/loading/hooks';
import { ConfirmationModalType } from 'services/modals/constants';

import { useAnalysisTemplatesColumn } from 'modules/analyse-templates/hooks/useAnalysisTemplatesColumn';
import { useSetDefaultTemplate } from 'hooks/useSetDefaultTemplate';

const container: SxProps = {
  p: 3
};

export const AnalyseTemplatesPage = () => {
  const navigate = useNavigate();
  const organization = useOrganization();
  const { showToast } = useToast();
  const { showModal } = useConfirmationModal();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const { setDefaultTemplate, organizationConfig } = useSetDefaultTemplate();

  const [search, setSearch] = useState('');

  const { sort, handleSort } = useTableSort<TemplateSortField, TemplateItem>({
    field: TemplateSortField.UPDATED_AT,
    direction: SearchableSortDirection.Desc
  });

  const { data, loading, refetch } =
    useGetAutomationTasksTemplateByOrganizationQuery({
      variables: {
        organizationId: organization.id,
        limit: 1000
      }
    });

  const [deleteAutomationTasksTemplateMutation] =
    useDeleteAutomationTasksTemplateMutation();

  const list = useMemo<TemplateItem[]>(() => {
    if (!data) {
      return [];
    }

    const result = extractQueryListItems(data);

    return result as TemplateItem[];
  }, [data]);

  const templatesFiltered = useMemo(() => {
    if (!list.length) {
      return [];
    }

    if (!search.length) {
      return list;
    }

    return list.filter((template) =>
      template.title.toLowerCase().includes(search.toLowerCase())
    );
  }, [list, search]);

  const handleEditTemplate = (templateId: string) => {
    navigate(`${Paths.ANALYSE_TEMPLATES}/${templateId}`);
  };

  const handleDeleteTemplate = (templateId: string) => {
    const templateToDelete = list.find(
      (template) => template.id === templateId
    );

    if (!templateToDelete) return;

    showModal({
      title: 'Delete template?',
      message: 'This cannot be undone',
      modalType: ConfirmationModalType.DESTRUCTIVE,
      cancelText: 'Cancel',
      destructiveText: 'Delete',
      onDestructiveConfirm: async () => {
        showLoadingOverlay();
        try {
          await deleteAutomationTasksTemplateMutation({
            variables: {
              input: {
                id: templateId
              }
            }
          });

          await refetch();

          showToast({
            title: 'Template deleted',
            type: ToastType.Success
          });
        } catch (e) {
          showToast({
            title: 'Something went wrong',
            message: (
              <FailedMessageWithHref
                failedMessage="Try deleting the template again."
                emailSubject="Failed to delete template"
              />
            ),
            type: ToastType.Error
          });
        } finally {
          hideLoadingOverlay();
        }
      }
    });
  };

  const handleSetAsDefault = (templateId: string) => {
    showModal({
      title: 'Set as default?',
      message: 'This template will be automatically selected for new analyses.',
      onConfirm: async () => {
        showLoadingOverlay();
        try {
          await setDefaultTemplate(templateId);
          showToast({
            title: 'Default template set',
            type: ToastType.Success
          });
        } catch (e) {
          showToast({
            title: 'Something went wrong',
            message: (
              <FailedMessageWithHref
                failedMessage="Try setting as a default template again."
                emailSubject="Failed to set default template"
              />
            ),
            type: ToastType.Error
          });
        } finally {
          hideLoadingOverlay();
        }
      }
    });
  };

  const columns = useAnalysisTemplatesColumn({
    handleEditTemplate,
    handleDeleteTemplate,
    handleSetAsDefault,
    defaultAutomationTaskTemplateId:
      organizationConfig?.defaultAutomationTaskTemplateId
  });

  return (
    <Stack direction="column" spacing={2} sx={container}>
      <Header
        breadcrumbs={[{ name: 'Analyse', path: Paths.ANALYSE }]}
        currentBreadcrumb="Templates"
        title="Templates"
        items={['Reusable settings for use across analyses']}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <SearchInput
          sx={{ width: 320 }}
          placeholder="Search templates"
          onChange={(e) => setSearch(e)}
        />
        <Button
          variant="primary"
          onClick={() => navigate(Paths.ANALYSE_TEMPLATE_CREATE)}
        >
          Create template
        </Button>
      </Stack>

      <TableV2
        data={templatesFiltered}
        isLoading={loading}
        columns={columns}
        pagination
        sortServer={false}
        onSort={handleSort}
        defaultSortFieldId={sort.field}
        defaultSortAsc={sort.direction === SearchableSortDirection.Asc}
        sortField={sort.field}
        emptyStateComponent={
          <Stack direction="column" alignItems="center" spacing={3}>
            <Typography fontSize="20px" fontWeight="600">
              No templates available
            </Typography>
            <Button
              variant="primary"
              onClick={() => navigate(Paths.ANALYSE_TEMPLATE_CREATE)}
            >
              Create your first one
            </Button>
          </Stack>
        }
      />
    </Stack>
  );
};
